/**
 * The R2 bucket's original public host, and the custom domain that now fronts it.
 *
 * Both serve the same objects (same bytes, `access-control-allow-origin: *`), but
 * `*.workers.dev` is a shared Cloudflare domain that some venue networks block outright.
 * On those booths every asset stored under the old host fails to load: the frame page
 * shows broken thumbnails and the print goes out without its frame (Bahela Kotak,
 * 2026-10-10, logged as `missing_frame_overlay:https://cdn.dreambooth-team.workers.dev/`).
 * Over the week before, 29 of 715 exports whose frame lived on workers.dev printed
 * frameless; 0 of 4,582 on cdn.dreambooth.app did.
 *
 * The Studio stopped minting the old host in 2025, but frames, stickers and filters
 * created before that (and everything the Bahela deployment created until 2026-10-10)
 * still store it, so the booth rewrites it on the way in.
 */
const LEGACY_CDN_PREFIX = 'https://cdn.dreambooth-team.workers.dev/'
const CDN_PREFIX = 'https://cdn.dreambooth.app/'

/** `https://cdn.dreambooth-team.workers.dev/x` → `https://cdn.dreambooth.app/x`; anything else as is. */
export function rewriteLegacyCdnUrl(url: string): string {
  return url.startsWith(LEGACY_CDN_PREFIX) ? CDN_PREFIX + url.slice(LEGACY_CDN_PREFIX.length) : url
}

/**
 * The old-host twin of a `cdn.dreambooth.app` URL, or null. Image caches keyed by URL hold
 * copies saved before the rewrite under the old host; this lets a lookup still find them.
 */
export function legacyCdnTwin(url: string): string | null {
  return url.startsWith(CDN_PREFIX) ? LEGACY_CDN_PREFIX + url.slice(CDN_PREFIX.length) : null
}

/** Deep copy of a JSON value (an API response) with every legacy CDN URL rewritten. */
export function rewriteLegacyCdnUrls<T>(value: T): T {
  if (typeof value === 'string') return rewriteLegacyCdnUrl(value) as T
  if (Array.isArray(value)) return value.map((item) => rewriteLegacyCdnUrls(item)) as T
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, rewriteLegacyCdnUrls(item)])
    ) as T
  }
  return value
}
